import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ErrorMessage, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import OtpInput from "react-otp-input";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { impersonateUser, impersonationHandoffQueryParam } from "./components/ImpersonationSlice";

type ImpersonationFormData = {
  verificationCode: string;
};

const initialValues: ImpersonationFormData = { verificationCode: "" };

const validationSchema = Yup.object().shape({
  verificationCode: Yup.string()
    .length(6, "Please enter the 6-digit verification code")
    .required("Verification code is required"),
});

const ImpersonationPage = () => {
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handoffReference = searchParams.get(impersonationHandoffQueryParam);

  const startImpersonation = useCallback(
    async (values: ImpersonationFormData, helpers: FormikHelpers<ImpersonationFormData>) => {
      try {
        if (!handoffReference) {
          throw new Error("No impersonation reference found in the query string");
        }

        // Impersonation tokens live in sessionStorage, separate from a normal login's
        // localStorage tokens, so there's no need to log out of a normal session first —
        // doing so would also clear that normal session in any other open tab.
        await thunkDispatch(impersonateUser({ verificationCode: values.verificationCode, handoffReference })).unwrap();
        navigate("/dashboard");
      } catch (error) {
        console.error(error);
        toast.error(
          "Unable to start impersonation session. This may mean the link has expired, already been used, or the verification code is incorrect."
        );
        navigate("/auth");
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [navigate, handoffReference, thunkDispatch]
  );

  return (
    <div className="d-flex flex-column flex-center flex-column-fluid p-10">
      <div className="w-lg-500px">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={startImpersonation}>
          {(formikProps) => (
            <Form autoComplete="off">
              <div className="text-center mb-10">
                <h1 className="heading-text cpl-text-primary mb-3">Verify Impersonation</h1>
              </div>
              <div className="fv-row mb-3">
                <div className="verification-code-section">
                  <div className="verification-code-label">Please enter the verification code to continue:</div>
                  <OtpInput
                    inputStyle="inputStyle"
                    value={formikProps.values.verificationCode}
                    onChange={(otp: string) => formikProps.setFieldValue("verificationCode", otp)}
                    numInputs={6}
                    inputType="number"
                    renderSeparator={(index) => (index === 2 ? <span>-</span> : "")}
                    renderInput={(props, index) => (
                      <input {...props} data-test-id={`impersonation-form__code-input-${index}`} />
                    )}
                  />
                </div>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <ErrorMessage name="verificationCode" />
                  </div>
                </div>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="cpl-btn btn-lg cpl-btn-primary w-100 mb-5"
                  disabled={formikProps.isSubmitting}
                  data-test-id="impersonation-form__submit-button"
                >
                  {formikProps.isSubmitting ? (
                    <span className="indicator-progress" style={{ display: "block" }}>
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    <span className="indicator-label">Verify</span>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export { ImpersonationPage };
